import { useRef } from 'react';
import { getTokenPxValue } from './getTokenPxValue';
function getElementFromRef(ref) {
    return 'current' in ref ? ref.current : ref;
}
export function getSide(anchorPoint) {
    const [side] = anchorPoint.split('-');
    return side;
}
const cachedPositions = new WeakMap();
export function useAnchorPosition(anchor, element, bounds = typeof document !== 'undefined'
    ? document.body
    : { current: null }) {
    const anchorPositionManager = useRef(null);
    if (anchorPositionManager.current === null) {
        anchorPositionManager.current = createAnchorPositionManager(anchor, element, bounds);
    }
    return anchorPositionManager.current;
}
export function getOppositeAnchor(anchor) {
    var _a;
    const [axis, alignment] = anchor.split('-');
    let oppositeAnchor = (_a = {
        top: 'bottom',
        bottom: 'top',
        left: 'right',
        right: 'left',
    }[axis]) !== null && _a !== void 0 ? _a : axis;
    if (alignment) {
        oppositeAnchor = `${oppositeAnchor}-${alignment}`;
    }
    return oppositeAnchor;
}
function createAnchorPositionManager(anchor, element, bounds) {
    function getPosition(anchorPoint, cache) {
        const $anchor = cache.get(anchor);
        const $element = cache.get(element);
        const $bounds = cache.get(bounds);
        if (!($element === null || $element === void 0 ? void 0 : $element.element) || !$anchor || !$bounds) {
            return {
                left: 0,
                top: 0,
                right: 0,
                bottom: 0,
            };
        }
        const { width = 0, height = 0 } = $element.element.getBoundingClientRect() || {};
        const { [anchorPoint]: $ } = $anchor.points;
        const isXCenter = /^(top|bottom)$/.test(anchorPoint);
        const isYCenter = /^(left|right)$/.test(anchorPoint);
        let left = $.x;
        let top = $.y;
        if (/^left|(top|bottom)-end$/.test(anchorPoint)) {
            left = $.x - width;
        }
        if (/^top|(right|left)-end$/.test(anchorPoint)) {
            top = $.y - height;
        }
        if (isXCenter) {
            left = $.x - width / 2;
        }
        if (isYCenter) {
            top = $.y - height / 2;
        }
        const offset = getTokenPxValue('--mollie-ui-floating-offset', $element.element, 0);
        const offsetX = getTokenPxValue('--mollie-ui-floating-offset-x', $element.element);
        const offsetY = getTokenPxValue('--mollie-ui-floating-offset-y', $element.element);
        const leftOffset = /^left/.test(anchorPoint) ? offsetX !== null && offsetX !== void 0 ? offsetX : offset : 0;
        const topOffset = /^top/.test(anchorPoint) ? offsetY !== null && offsetY !== void 0 ? offsetY : offset : 0;
        const rightOffset = /^right/.test(anchorPoint) ? offsetX !== null && offsetX !== void 0 ? offsetX : offset : 0;
        const bottomOffset = /^bottom/.test(anchorPoint) ? offsetY !== null && offsetY !== void 0 ? offsetY : offset : 0;
        left = left - leftOffset + rightOffset;
        top = top - topOffset + bottomOffset;
        const right = left + width;
        const bottom = top + height;
        return {
            left: Math.round(left),
            top: Math.round(top),
            right: Math.round(right),
            bottom: Math.round(bottom),
        };
    }
    return function getNearestAnchor(options = {}) {
        const { placement = 'top', preferFlip, size } = options;
        const cache = new Map();
        const $anchor = getElementFromRef(anchor);
        const $element = getElementFromRef(element);
        const $bounds = getElementFromRef(bounds);
        cache.set(anchor, {
            element: $anchor,
            points: {},
        });
        cache.set(element, {
            element: $element,
            points: {},
        });
        cache.set(bounds, {
            element: $bounds,
            points: {},
        });
        if (!$anchor || !$bounds || !$element) {
            return null;
        }
        const { left: origLeft, position: origPosition } = window.getComputedStyle($element);
        const visible = $element.offsetParent != null;
        if (!visible) {
            $element.style.setProperty('display', 'initial');
            if (origPosition !== 'fixed' && origPosition !== 'absolute') {
                $element.style.setProperty('position', 'absolute');
            }
            $element.style.setProperty('left', '-999rem');
        }
        const anchorPoints = createPoints($anchor);
        const boundsPoints = createPoints($bounds);
        cache.set(anchor, {
            element: $anchor,
            points: createPoints($anchor),
        });
        cache.set(bounds, {
            element: $bounds,
            points: createPoints($bounds),
        });
        const safeOffset = getTokenPxValue('--mollie-ui-floating-safe-offset', $element, getTokenPxValue('--mollie-ui-floating-offset', $element, 0));
        const boundsPosition = {
            left: Math.max(boundsPoints.left.x, 0) + safeOffset,
            right: Math.min(boundsPoints.right.x, window.innerWidth) - safeOffset,
            top: Math.max(boundsPoints.top.y, 0) + safeOffset,
            bottom: Math.min(boundsPoints.bottom.y, window.innerHeight) - safeOffset,
        };
        const { left: anchorLeft, top: anchorTop } = getPosition(placement, cache);
        const anchorsSorted = Object.keys(anchorPoints)
            .map(($point) => {
            var _a, _b, _c;
            const styles = {};
            if (size) {
                const [axis, alignment] = $point.split('-');
                const xAxis = /left|right/.test(axis)
                    ? axis
                    : (_a = { start: 'left', end: 'right' }[alignment]) !== null && _a !== void 0 ? _a : 'center';
                const yAxis = /top|bottom/.test(axis)
                    ? axis
                    : (_b = { start: 'top', end: 'bottom' }[alignment]) !== null && _b !== void 0 ? _b : 'center';
                let availableHeight = 0;
                let availableWidth = 0;
                switch (xAxis) {
                    case 'left': {
                        availableWidth = anchorPoints.left.x - boundsPosition.left;
                        break;
                    }
                    case 'right': {
                        availableWidth = boundsPosition.right - anchorPoints.right.x;
                        break;
                    }
                    case 'center': {
                        const left = anchorPoints.left.x - boundsPosition.left;
                        const right = boundsPosition.right - anchorPoints.right.x;
                        availableWidth =
                            anchorPoints.right.x -
                                anchorPoints.left.x +
                                Math.min(left, right);
                    }
                }
                switch (yAxis) {
                    case 'top': {
                        availableHeight = anchorPoints.top.y - boundsPosition.top;
                        break;
                    }
                    case 'bottom': {
                        availableHeight = boundsPosition.bottom - anchorPoints.bottom.y;
                        break;
                    }
                    case 'center': {
                        const left = anchorPoints.top.y - boundsPosition.top;
                        const right = boundsPosition.bottom - anchorPoints.bottom.y;
                        availableHeight =
                            anchorPoints.bottom.y -
                                anchorPoints.top.y +
                                Math.min(left, right);
                    }
                }
                Object.assign(styles, size({
                    elements: {
                        anchor: $anchor,
                        floating: $element,
                    },
                    availableHeight: availableHeight - safeOffset * 2,
                    availableWidth: availableWidth - safeOffset * 2,
                }));
            }
            const cachedStyles = {};
            for (const [property, value] of Object.entries(styles)) {
                cachedStyles[property] = $element.style.getPropertyValue(property);
                $element.style.setProperty(property, value);
            }
            const { width = 0, height = 0 } = $element.getBoundingClientRect() || {};
            let position = getPosition($point, cache);
            // reset styles to their original value
            for (const property of Object.keys(styles)) {
                $element.style.setProperty(property, cachedStyles[property]);
            }
            const isWithinBounds = (_position, _boundsPosition = boundsPosition) => {
                return (_position.left >= _boundsPosition.left &&
                    _position.right <= _boundsPosition.right &&
                    _position.top >= _boundsPosition.top &&
                    _position.bottom <= _boundsPosition.bottom);
            };
            let withinBounds = isWithinBounds(position);
            let hasShifted = false;
            const side = getSide($point);
            const max = { ...position };
            if (side === 'top' || side === 'bottom') {
                max.left = boundsPosition.left;
                max.right = boundsPosition.right;
            }
            if (side === 'left' || side === 'right') {
                max.top = boundsPosition.top;
                max.bottom = boundsPosition.bottom;
            }
            if (!withinBounds && !isWithinBounds(position, max)) {
                const side = getSide($point);
                const isYAxis = /top|bottom/.test(side);
                const diffs = {
                    left: position.left - boundsPosition.left,
                    right: boundsPosition.right - position.right,
                    top: position.top - boundsPosition.top,
                    bottom: boundsPosition.bottom - position.bottom,
                };
                const max = { ...position };
                if (side === 'top' || side === 'bottom') {
                    max.left = anchorPoints.left.x - width - safeOffset;
                    max.right = anchorPoints.right.x + width + safeOffset;
                }
                if (side === 'left' || side === 'right') {
                    max.top = anchorPoints.top.y - height - safeOffset;
                    max.bottom = anchorPoints.bottom.y + height + safeOffset;
                }
                const sides = isYAxis
                    ? ['left', 'right']
                    : ['top', 'bottom'];
                const shiftedPosition = (_c = Array.from(sides)
                    .map((side) => {
                    if (side === 'left' || side === 'right') {
                        const diff = position.left < boundsPosition.left
                            ? Math.abs(diffs[side])
                            : diffs[side];
                        return {
                            ...position,
                            left: position.left + diff,
                            right: position.right + diff,
                        };
                    }
                    const diff = position.top < boundsPosition.top
                        ? Math.abs(diffs[side])
                        : diffs[side];
                    return {
                        ...position,
                        top: position.top + diff,
                        bottom: position.bottom + diff,
                    };
                })
                    .map((position) => {
                    const distance = Math.sqrt(Math.pow(position.left - anchorLeft, 2) +
                        Math.pow(position.top - anchorTop, 2));
                    return {
                        distance,
                        position,
                    };
                })
                    .filter(({ position }) => {
                    if (!isWithinBounds(position, max)) {
                        return false;
                    }
                    return true;
                })
                    .sort((a, b) => {
                    return a.distance - b.distance;
                })[0]) === null || _c === void 0 ? void 0 : _c.position;
                if (shiftedPosition) {
                    position = shiftedPosition;
                    withinBounds = isWithinBounds(position);
                    hasShifted = true;
                }
            }
            const distance = Math.sqrt(Math.pow(position.left - anchorLeft, 2) +
                Math.pow(position.top - anchorTop, 2));
            return {
                distance,
                point: $point,
                position,
                withinBounds,
                hasShifted,
                styles,
            };
        })
            .sort((a, b) => {
            return a.distance - b.distance;
        });
        const anchorsFiltered = anchorsSorted.filter(({ withinBounds }) => !!withinBounds);
        let placementAnchor;
        let oppositeAnchor;
        let cachedAnchor;
        for (let i = 0; i < anchorsSorted.length; i++) {
            if (anchorsSorted[i].withinBounds &&
                anchorsSorted[i].point === placement) {
                placementAnchor = anchorsSorted[i];
            }
            if (anchorsSorted[i].withinBounds &&
                anchorsSorted[i].point === getOppositeAnchor(placement)) {
                oppositeAnchor = anchorsSorted[i];
            }
            if (anchorsSorted[i].point === cachedPositions.get($anchor)) {
                cachedAnchor = anchorsSorted[i];
            }
        }
        const nearestAnchor = placementAnchor ||
            (preferFlip && oppositeAnchor) ||
            anchorsFiltered[0] ||
            cachedAnchor ||
            anchorsSorted[0];
        cachedPositions.set($anchor, nearestAnchor.point);
        let strategy = /fixed|sticky/.test(origPosition)
            ? 'fixed'
            : 'absolute';
        if (strategy !== 'fixed') {
            let scrollTop = 0;
            let scrollLeft = 0;
            let currentElement = $anchor;
            let css;
            while (currentElement.offsetParent) {
                currentElement = currentElement.offsetParent;
                css = window.getComputedStyle(currentElement);
                if (css.getPropertyValue('position') !== 'fixed') {
                    scrollLeft = scrollLeft + currentElement.scrollLeft;
                    scrollTop = scrollTop + currentElement.scrollTop;
                }
                if (css.getPropertyValue('position') === 'relative') {
                    break;
                }
                strategy = /fixed|sticky/.test(css.getPropertyValue('position'))
                    ? 'fixed'
                    : strategy;
            }
            if ((css === null || css === void 0 ? void 0 : css.getPropertyValue('position')) !== 'fixed') {
                scrollLeft = scrollLeft + document.documentElement.scrollLeft;
                scrollTop = scrollTop + document.documentElement.scrollTop;
            }
            else {
                strategy = 'fixed';
            }
            // reset scroll position when strategy is fixed
            if (strategy === 'fixed') {
                scrollLeft = 0;
                scrollTop = 0;
            }
            nearestAnchor.position = {
                left: scrollLeft + nearestAnchor.position.left,
                right: scrollLeft + nearestAnchor.position.right,
                top: scrollTop + nearestAnchor.position.top,
                bottom: scrollTop + nearestAnchor.position.bottom,
            };
        }
        if (!visible) {
            $element.style.setProperty('display', '');
            $element.style.setProperty('left', origLeft);
            $element.style.setProperty('position', origPosition);
        }
        return {
            ...nearestAnchor,
            strategy,
        };
    };
}
function createPoints(element) {
    const { top, left, bottom, right, width, height } = element.getBoundingClientRect();
    const centerX = left + width / 2;
    const centerY = top + height / 2;
    return {
        'top-start': {
            x: left,
            y: top,
        },
        top: {
            x: centerX,
            y: top,
        },
        'top-end': {
            x: right,
            y: top,
        },
        'right-start': {
            x: right,
            y: top,
        },
        right: {
            x: right,
            y: centerY,
        },
        'right-end': {
            x: right,
            y: bottom,
        },
        'bottom-end': {
            x: right,
            y: bottom,
        },
        bottom: {
            x: centerX,
            y: bottom,
        },
        'bottom-start': {
            x: left,
            y: bottom,
        },
        'left-end': {
            x: left,
            y: bottom,
        },
        left: {
            x: left,
            y: centerY,
        },
        'left-start': {
            x: left,
            y: top,
        },
    };
}
//# sourceMappingURL=useAnchorPosition.js.map